/**
 * Saves many guests' event invitations at once: PUT /planning/:project_id/guest-rsvps/bulk (planner and above).
 * The API takes at most 200 guests per call and writes a call all or nothing, so changes go in chunks of 200, in order,
 * and the first refused chunk stops the rest. Guests in the chunks before it are saved.
 */
import { apiPut, planningPath } from './PlanningApiClient';
import { errorMessageOf, isApiError } from '../utils/apiErrors';
import { chunk, MAX_GUESTS_PER_CALL, type BulkChange, type EventAnswers, type RsvpAnswer } from '../utils/bulkRsvp';

/**
 * A guest who changed after the screen loaded them, as the 409 `rsvp_conflict` body carries them (the guest's CURRENT
 * state: name, updated_at, the status the server derived, and the per-event answers). Every field except the id is
 * optional, so an older API that sends only ids still works: the screen then falls back to its own data.
 */
export interface ConflictGuest {
  guestId: string;
  name?: string;
  updatedAt?: string;
  /** The guest-level status the server derived. Never worked out on the client. */
  status?: string;
  /** Event id to answer, null where not invited. Undefined when the API did not send it. */
  events?: EventAnswers;
}

export type BulkFailure =
  | { kind: 'conflict'; guestIds: string[]; conflicts: ConflictGuest[]; /** How many guests conflict in all; more than are listed when the API cut the list. */ total: number; message: string }
  | { kind: 'checked_in'; guestIds: string[]; message: string }
  | { kind: 'unknown'; guestIds: string[]; eventIds: string[]; message: string }
  | { kind: 'other'; message: string };

export interface BulkSaveResult {
  /** Guests whose invitations really changed. */
  savedCount: number;
  /** Every guest sent in a chunk the API accepted: their answers are now as the user set them. */
  savedGuestIds: string[];
  failure: BulkFailure | null;
}

interface BulkResponse { guests?: Array<{ guest_id: string }> }

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

const answerOf = (v: unknown): RsvpAnswer => (v === 'confirmed' || v === 'declined' ? v : v === null || v === undefined ? null : 'pending');

function conflictOf(raw: unknown): ConflictGuest | null {
  const c = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (typeof c.guest_id !== 'string') return null;
  const events = c.events && typeof c.events === 'object'
    ? Object.fromEntries(Object.entries(c.events as Record<string, unknown>).map(([id, a]) => [id, answerOf(a)]))
    : undefined;
  const at = typeof c.updated_at === 'string' ? c.updated_at : typeof c.changed_at === 'string' ? c.changed_at : undefined;
  const status = typeof c.rsvp_status === 'string' ? c.rsvp_status : typeof c.derived_status === 'string' ? c.derived_status : undefined;
  return { guestId: c.guest_id, name: typeof c.name === 'string' ? c.name : undefined, updatedAt: at, status, events };
}

function failureOf(err: unknown): BulkFailure {
  const message = errorMessageOf(err, 'The RSVPs could not be saved. Please try again.');
  if (isApiError(err)) {
    const body = (err.body && typeof err.body === 'object' ? err.body : {}) as Record<string, unknown>;
    if (err.code === 'rsvp_conflict') {
      const conflicts = (Array.isArray(body.conflicts) ? body.conflicts : []).map(conflictOf).filter((c): c is ConflictGuest => c !== null);
      const total = typeof body.conflict_count === 'number' ? Math.max(body.conflict_count, conflicts.length) : conflicts.length;
      return { kind: 'conflict', guestIds: conflicts.map(c => c.guestId), conflicts, total, message };
    }
    if (err.code === 'checked_in') return { kind: 'checked_in', guestIds: strings(body.guest_ids), message };
    if (err.code === 'unknown_ids') return { kind: 'unknown', guestIds: strings(body.unknown_guest_ids), eventIds: strings(body.unknown_event_ids), message };
  }
  return { kind: 'other', message };
}

export async function saveBulkRsvps(changes: readonly BulkChange[]): Promise<BulkSaveResult> {
  let savedCount = 0;
  const savedGuestIds: string[] = [];
  for (const part of chunk(changes, MAX_GUESTS_PER_CALL)) {
    try {
      const res = await apiPut<BulkResponse>(planningPath('/guest-rsvps/bulk'), { changes: part });
      savedCount += res?.guests?.length ?? part.length;
      savedGuestIds.push(...part.map(c => c.guest_id));
    } catch (err) {
      return { savedCount, savedGuestIds, failure: failureOf(err) };
    }
  }
  return { savedCount, savedGuestIds, failure: null };
}
